import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useCustomer } from "../context/CustomerContext";
import M365AutoConnectModal from "../components/M365AutoConnectModal";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";
import M365TenantHealth from "../components/M365TenantHealth";

type Tenant = {
  id: number;
  customer_id: number;
  tenant_id: string;
  client_id: string;
  display_name: string | null;
  has_secret: boolean;
  is_active: boolean;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
};

type Customer = { id: number; name: string };

export default function M365Tenants() {
  // Honor the global customer switcher (no local customer picker). When a
  // customer is selected the page scopes to it; "All customers" shows the full
  // overview but add/connect is disabled (pick a customer at the top first).
  const { currentCustomerId } = useCustomer();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const sort = useSort("customer");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Cloud tenants",
    useMemo(
      () =>
        tenants.map((t: any) => ({
          customer: t.customer_name ?? null,
          tenant_domain: t.tenant_domain ?? null,
          connected: t.connected ?? null,
          last_sync_at: t.last_sync_at ?? null,
          users: t.user_count ?? null,
          // NOT client_secret / certificate thumbprints - this screen holds
          // tenant credentials and none of them belong in a prompt.
        })),
      [tenants],
    ),
    undefined,
  );

  const refresh = async () => {
    setBusy(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/api/v1/m365/tenants"),
        api.get("/api/v1/customers"),
      ]);
      setTenants(t.data);
      setCustomers(c.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const sync = async (id: number) => {
    setBusy(true);
    try {
      const r = await api.post(`/api/v1/m365/tenants/${id}/sync`);
      alert(
        r.data.ok
          ? `Sync OK — users=${r.data.users} subscriptions=${r.data.subscriptions}`
          : `Sync failed: ${r.data.reason || r.data.detail || "unknown"}`
      );
      await refresh();
    } catch (e: any) {
      alert("Sync failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this M365 tenant config?")) return;
    await api.delete(`/api/v1/m365/tenants/${id}`);
    await refresh();
  };

  // Download the PowerShell onboarding script (creates the app registration +
  // grants admin consent + mints a secret in the customer's tenant). Fetched
  // through the authed api client, then saved as a .ps1.
  const downloadScript = async (customerId?: number, fmt: "cmd" | "ps1" = "cmd") => {
    try {
      const params: Record<string, string | number> = { format: fmt };
      if (customerId) params.customer_id = customerId;
      const r = await api.get("/api/v1/m365/tenants/setup-script", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "ps1" ? "AeroGauge-M365-Setup.ps1" : "AeroGauge-M365-Setup.cmd";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Could not generate script");
    }
  };

  const customerName = (cid: number) =>
    customers.find((c) => c.id === cid)?.name || `#${cid}`;

  // Scope the list to the top-selected customer; show all in the "All" view.
  const visible = currentCustomerId
    ? tenants.filter((t) => t.customer_id === currentCustomerId)
    : tenants;
  const selectedHasTenant =
    currentCustomerId != null && tenants.some((t) => t.customer_id === currentCustomerId);

  const sortedVisible = sortRows(visible, sort.sort, (t, key) => {
    switch (key) {
      case "display_name":
        return (t.display_name || "").toLowerCase();
      case "tenant_id":
        return (t.tenant_id || "").toLowerCase();
      case "client_id":
        return (t.client_id || "").toLowerCase();
      case "is_active":
        return t.is_active ? 1 : 0;
      case "last_sync_at":
        return t.last_sync_at ? Date.parse(t.last_sync_at) : 0;
      default:
        return customerName(t.customer_id).toLowerCase();
    }
  });

  return (
    <div className="space-y-4">
      {/* STACKED, not side-by-side. These four buttons and this much prose
          never fit one row: originally the buttons had no shrink-0 and
          squeezed the text into a column with the hint reduced to one word
          per line; giving them shrink-0 just moved the squeeze onto the
          prose instead. The heading gets the full width, the actions get
          their own row underneath. */}
      <div className="space-y-3">
        <div className="max-w-4xl">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Microsoft 365 Tenants
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Per-customer M365 app registrations. Client secrets stored encrypted (AES-256-GCM).
            Sync runs every 6 hours; "Sync now" forces it immediately for one tenant.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            New tenant? Download the setup script (a double-click .cmd — no execution-policy
            steps) and run it once as a Global Admin of the customer's tenant. It creates the app
            registration, grants admin consent, and prints the three values to paste here.
            <strong> If users/licenses aren't showing, admin consent is almost always the cause —
            run the script.</strong>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setAutoOpen(true)}
            disabled={!currentCustomerId}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-medium disabled:opacity-40"
            title="Run the one-click tool and the tenant connects itself — no copy-paste"
          >
            {selectedHasTenant ? "Re-connect tenant" : "Auto-connect tenant"}
          </button>
          <button
            onClick={() => downloadScript(currentCustomerId ?? undefined, "cmd")}
            disabled={!currentCustomerId}
            className="px-3 py-1.5 border border-blue-600 text-blue-700 hover:bg-blue-50 rounded text-sm font-medium disabled:opacity-40"
            title="Manual: creates the app + grants consent + prints the three values to paste in"
          >
            Manual setup script
          </button>
          <button
            onClick={() => downloadScript(currentCustomerId ?? undefined, "ps1")}
            disabled={!currentCustomerId}
            className="px-2 py-1.5 text-xs text-gray-500 hover:underline disabled:opacity-40"
            title="Raw PowerShell .ps1 (run with -ExecutionPolicy Bypass)"
          >
            .ps1
          </button>
          {!selectedHasTenant && (
            <button
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              disabled={!currentCustomerId}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-40"
            >
              + Add tenant manually
            </button>
          )}
          {!currentCustomerId && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Select a customer at the top to add or connect a tenant
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Under the table on purpose: the list is what an admin came for,
          and this explains what each row can actually DO. Eleven tenants sat
          half-configured because 'connected' was the only thing on screen. */}
      <M365TenantHealth />

      <div
        className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100
                   dark:border-gray-700 shadow-sm overflow-x-auto"
      >
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Customer" sortKey="customer" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Display" sortKey="display_name" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Tenant ID" sortKey="tenant_id" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Client ID" sortKey="client_id" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Active" sortKey="is_active" sort={sort.sort} onSort={sort.toggle} />
              <SortableTh label="Last sync" sortKey="last_sync_at" sort={sort.sort} onSort={sort.toggle} />
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-400 dark:text-gray-500">
                  {currentCustomerId
                    ? "No tenant configured for this customer. Use Auto-connect tenant above."
                    : "No tenants configured. Select a customer at the top, then Auto-connect."}
                </td>
              </tr>
            ) : (
              sortedVisible.map((t) => (
                <tr
                  key={t.id}
                  className="border-b dark:border-gray-700 last:border-0 text-gray-800
                             dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <td className="py-2 px-3">{customerName(t.customer_id)}</td>
                  <td className="py-2 px-3">{t.display_name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{t.tenant_id}</td>
                  <td className="py-2 px-3 font-mono text-xs">{t.client_id}</td>
                  <td className="py-2 px-3">{t.is_active ? "✓" : ""}</td>
                  <td className="py-2 px-3 text-xs">
                    {t.last_sync_status === "success" ? (
                      <span className="text-green-700">
                        ✓ {t.last_sync_at && formatDateTime(t.last_sync_at)}
                      </span>
                    ) : t.last_sync_status === "failure" ? (
                      <span className="text-red-700" title={t.last_sync_error || ""}>
                        ✗ {t.last_sync_at && formatDateTime(t.last_sync_at)}
                      </span>
                    ) : (
                      <span className="text-gray-400">never</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right space-x-2">
                    <button
                      className="text-xs text-violet-600 hover:underline"
                      onClick={() => sync(t.id)}
                      disabled={busy}
                    >
                      Sync now
                    </button>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        setEditing(t);
                        setShowModal(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => downloadScript(t.customer_id)}
                      title="Re-run setup / fix admin consent for this customer's tenant"
                    >
                      Setup script
                    </button>
                    <button
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => remove(t.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && currentCustomerId && (
        <TenantModal
          editing={editing}
          customerId={currentCustomerId}
          customerName={customerName(currentCustomerId)}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            refresh();
          }}
        />
      )}

      {autoOpen && currentCustomerId && (
        <M365AutoConnectModal
          customerId={currentCustomerId}
          customerName={customerName(currentCustomerId)}
          onClose={() => setAutoOpen(false)}
          onDone={() => refresh()}
        />
      )}
    </div>
  );
}

function TenantModal({
  editing,
  customerId,
  customerName,
  onClose,
  onSaved,
}: {
  editing: Tenant | null;
  customerId: number;
  customerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tenantId, setTenantId] = useState(editing?.tenant_id || "");
  const [clientId, setClientId] = useState(editing?.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [displayName, setDisplayName] = useState(editing?.display_name || "");
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: any = {
        tenant_id: tenantId,
        client_id: clientId,
        display_name: displayName || null,
        is_active: isActive,
      };
      if (clientSecret) body.client_secret = clientSecret;
      if (editing) {
        await api.put(`/api/v1/m365/tenants/${editing.id}`, body);
      } else {
        if (!clientSecret) {
          setError("Client secret required on create");
          setBusy(false);
          return;
        }
        await api.post("/api/v1/m365/tenants", {
          ...body,
          customer_id: customerId,
          client_secret: clientSecret,
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl p-5 w-full max-w-lg space-y-3">
        <h3 className="text-lg font-semibold">
          {editing ? "Edit M365 tenant" : "Add M365 tenant"}
        </h3>
        {!editing && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Customer: <span className="font-medium text-gray-900">{customerName}</span>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium mb-1">Display name</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="Acme Corp production tenant"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Tenant ID</label>
          <input
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Client ID</label>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Client secret {editing && "(leave blank to keep)"}
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            className="w-full border rounded px-2 py-1.5 text-sm font-mono"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active
        </label>
        {error && (
          <div className="text-sm bg-red-50 text-red-800 p-2 rounded">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {editing ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
